import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import ActionsDropdown from '../../../../../features/components/ActionsDropdown'
import AdCardListItem from '../../../../../features/components/AdCardListItem'
import SkeletonAdsGalleryItem from './Skeleton'
import InternalAdActionButtons from '../../../../AdApprovals/AdsSection/InternalAdActionButtons'
import AdStatusChip from '../../FacebookAdsPreviewList/FacebookAdCardListItem/AdCardStatuses/AdStatusChip'

import useGetAdDropdownOptions from '../../hooks/useGetAdDropdownOptions'
import useInternalAdSelfAccountName from '../../../../../features/hooks/internalAdHooks/useInternalAdSelfAccountName'
import { useClearApproveInternalAd } from '../../../../AdApprovals/AdsSection/hooks'

import { approveInternalAd } from '../../../../../modules/actions/ads'
import { openForm } from '../../../../../modules/actions/forms'
import { selectedDV360AdAccountSelector } from '../../../../../modules/selectors/adAccounts'
import {
  adUpdateIdSelector,
  adUpdateIsLoadingSelector,
  approveInternalAdIsLoadingSelector,
  disapproveInternalAdIsLoadingSelector
} from '../../../../../modules/selectors/ads'

import { DV_360_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { AD_DV360_EDIT } from '../../../../../constants/forms'

import useStyles from './styles'

const AdsGalleryItem = ({
  photo: ad,
  wrapperStyle,
  showDetails = true,
  showActionsDropdown = true,
  showInternalAdActionButtons,
  disapproveInternalAdOpenModalHandler
}) => {
  const classes = useStyles()

  const dispatch = useDispatch()

  const adUpdateId = useSelector(adUpdateIdSelector)
  const adUpdateIsLoading = useSelector(adUpdateIsLoadingSelector)

  const { id: dv360AdAccountId } = useSelector(selectedDV360AdAccountSelector)

  const { id, name, internal: isAdInternal } = ad

  const internalAdIsApproving = useSelector(approveInternalAdIsLoadingSelector(id))
  const internalAdIsDisapproving = useSelector(disapproveInternalAdIsLoadingSelector(id))
  useClearApproveInternalAd(id)

  const adSelfAccountName = useInternalAdSelfAccountName(ad)

  const isAdItemUpdating = adUpdateIsLoading && adUpdateId === id

  const getAdDropdownOptions = useGetAdDropdownOptions(DV_360_PLATFORM, dv360AdAccountId)
  const dropdownOptions = useMemo(() => getAdDropdownOptions(ad), [ad, getAdDropdownOptions])

  const approveInternalAdHandler = useCallback(() => {
    dispatch(approveInternalAd(id))
  }, [dispatch, id])

  const editInternalAdHandler = useCallback(() => {
    dispatch(openForm({ id, formName: AD_DV360_EDIT, otherParams: 'internal=true' }))
  }, [dispatch, id])

  const disapproveInternalAdHandler = useCallback(() => {
    disapproveInternalAdOpenModalHandler(ad)
  }, [ad, disapproveInternalAdOpenModalHandler])

  const renderSubTitle = useCallback(() => {
    if (showInternalAdActionButtons) {
      return adSelfAccountName
    } else {
      return <AdStatusChip ad={ad} />
    }
  }, [showInternalAdActionButtons, ad, adSelfAccountName])

  // While loading/updating/approving/disapproving show skeleton with preserved size
  if (isAdItemUpdating || internalAdIsApproving || internalAdIsDisapproving) {
    return (
      <SkeletonAdsGalleryItem
        photo={ad}
        wrapperStyle={wrapperStyle}
        showDetails={showDetails}
        showInternalAdActionButtonsSkeleton={showInternalAdActionButtons}
      />
    )
  }

  // Fallback if wrapperStyle is not provided (should be provided by RPA v3)
  const outerStyle = wrapperStyle || {
    position: 'relative',
    width: '100%'
  }

  return (
    <div style={outerStyle}>
      <AdCardListItem>
        {/* Render the image using react-photo-album v3-provided imageProps */}
        {ad?.src && (
          <img
            src={ad.src}
            style={{
              display: 'block',
              width: '100%',
              height: 'auto'
            }}
            alt={name}
          />
        )}

        {showDetails && (
          <div className={classes.adDetails}>
            <div className={classes.adDetailsLeftColumn}>
              <h4 className={classes.adName}>{name}</h4>
              {renderSubTitle()}
            </div>
            {showActionsDropdown && !!dropdownOptions?.length && <ActionsDropdown options={dropdownOptions} />}
          </div>
        )}
        {showInternalAdActionButtons && isAdInternal && (
          <InternalAdActionButtons
            className={classes.internalAdActionButtons}
            approveInternalAdHandler={approveInternalAdHandler}
            editInternalAdHandler={editInternalAdHandler}
            disapproveInternalAdHandler={disapproveInternalAdHandler}
          />
        )}
      </AdCardListItem>
    </div>
  )
}

AdsGalleryItem.propTypes = {
  showDetails: PropTypes.bool,
  showActionsDropdown: PropTypes.bool,
  showInternalAdActionButtons: PropTypes.bool,
  disapproveInternalAdOpenModalHandler: PropTypes.func
}

export default AdsGalleryItem
